export const onboardingConfig = {
  // Phase 1: Foundational Setup
  phase1: {
    checklist: [
      {
        id: 'customize_settings',
        title: 'Customize your settings',
        tour: 'settingsCustomizationTour',
        check: 'settingsAreCustomized',
        help: 'Go to Settings page and fill in your company name, email, and other basic information. This helps personalize your quotes and communications.',
      },
      {
        id: 'connect_gmail',
        title: 'Connect Gmail Account',
        tour: 'gmailConnectionTour',
        check: 'gmailIsConnected',
        help: 'Navigate to Settings → Email → Connect Gmail. Follow the OAuth flow to securely link your Gmail account for sending quotes and invoices.',
      },
      {
        id: 'create_client',
        title: 'Create your first client',
        tour: 'clientCreationTour',
        check: 'clientsExist',
        help: 'Go to Clients → New Client button. Enter the client\'s name, phone, email, and address. Save to complete this step.',
      },
      {
        id: 'create_quote',
        title: 'Draft a new quote',
        tour: 'quoteCreationTour',
        check: 'quotesExist',
        help: 'Navigate to Quotes → New Quote. Select your client, add a project name, and add at least one line item (service or product). Save as draft.',
      },
      {
        id: 'send_quote',
        title: 'Send your first quote',
        tour: 'quoteSendingTour',
        check: 'quoteIsSent',
        help: 'Open your draft quote and click "Send Quote". Confirm the recipient email and click "Send" to deliver it to your client via email.',
      },
    ],
  },

  // Phase 2: Advanced Features
  phase2: {
    checklist: [
      {
        id: 'schedule_appointment',
        title: 'Schedule an appointment',
        tour: 'appointmentSchedulingTour',
        check: 'appointmentIsScheduled',
        help: 'Go to Appointments → New Appointment. Select a client, choose preferred date/time, and add appointment details. You can also request appointments from clients in the pipeline.',
      },
      {
        id: 'create_section_template',
        title: 'Create a section template',
        tour: 'sectionTemplateCreationTour',
        check: 'sectionTemplateExists',
        help: 'Navigate to Settings → Section Templates → New Template. Create reusable service packages (e.g., "Basic Kitchen Remodel", "Premium Bathroom"). Add line items with pricing for quick quoting.',
      },
      {
        id: 'use_section_template',
        title: 'Use a section template',
        tour: 'sectionTemplateUsageTour',
        check: 'quoteUsesTemplate',
        help: 'When creating a quote, click "Add from Template" and select a pre-built section. This saves time and ensures consistent pricing across similar projects.',
      },
      {
        id: 'edit_email_templates',
        title: 'Customize email templates',
        tour: 'emailTemplateEditingTour',
        check: 'emailTemplatesAreEdited',
        help: 'Go to Settings → Email Templates. Customize the default quote and invoice emails with your branding, signature, and messaging to match your business voice.',
      },
      {
        id: 'proposal_accepted',
        title: 'Get first proposal accepted',
        tour: 'proposalAcceptanceTour',
        check: 'proposalIsAccepted',
        help: 'Send a quote to a client. Once they accept (either via email link or dashboard), it automatically converts to an invoice and moves to your active projects.',
      },
    ],
  },
  tours: {
    // Phase 1 Tours
    clientCreationTour: [
      {
        target: '.new-client-button',
        content: 'First, let\'s add a client. Click here to start.',
      },
      {
        target: '#client-name-input',
        content: 'Give your client a name. It can be a real client or just a test.',
      },
      {
        target: '#client-save-button',
        content: 'Once you are done, save the client.',
      },
    ],
    quoteCreationTour: [
      {
        target: '.new-quote-button',
        content: 'Great! Now, let\'s create a quote for that client.',
      },
      {
        target: '#quote-client-selector',
        content: 'Select the client you just created.',
      },
      {
        target: '#quote-name-input',
        content: 'Give your quote a name, like "Kitchen Remodel".',
      },
      {
        target: '.add-line-item-button',
        content: 'Add a line item to your quote.',
      },
      {
        target: '#quote-save-button',
        content: 'Save the draft. Next, we will customize it.',
      },
    ],
    quoteCustomizationTour: [
      {
        target: '.edit-quote-button',
        content: 'Let\'s customize the quote you just created.',
      },
      {
        target: '.branding-options',
        content: 'You can add your company logo and branding here.',
      },
      {
        target: '.terms-and-conditions',
        content: 'Don\'t forget to add your terms and conditions.',
      }
    ],
    quoteSendingTour: [
      {
        target: '.send-quote-button',
        content: 'Now you are ready to send the quote to your client.',
      },
      {
        target: '#recipient-email-input',
        content: 'Confirm the recipient\'s email address.',
      },
      {
        target: '.send-email-button',
        content: 'Click here to send the quote!',
      }
    ],
    settingsCustomizationTour: [
      {
        target: '.settings-nav-link',
        content: 'Navigate to your Settings page.',
      },
      {
        target: '#company-name-input',
        content: 'Enter your company name.',
      },
      {
        target: '#company-email-input',
        content: 'Add your business email.',
      },
    ],
    gmailConnectionTour: [
      {
        target: '.settings-nav-link',
        content: 'Go to Settings.',
      },
      {
        target: '.email-settings-tab',
        content: 'Click on Email Settings.',
      },
      {
        target: '.connect-gmail-button',
        content: 'Click to authorize Gmail.',
      },
    ],

    // Phase 2 Tours
    appointmentSchedulingTour: [
      {
        target: '.appointments-nav-link',
        content: 'Navigate to your Appointments page.',
      },
      {
        target: '.new-appointment-button',
        content: 'Click to create a new appointment.',
      },
      {
        target: '#appointment-client-selector',
        content: 'Select the client for this appointment.',
      },
      {
        target: '#appointment-date-input',
        content: 'Choose your preferred appointment date.',
      },
      {
        target: '#appointment-time-input',
        content: 'Select your preferred time.',
      },
      {
        target: '.schedule-button',
        content: 'Confirm and schedule the appointment.',
      },
    ],
    sectionTemplateCreationTour: [
      {
        target: '.settings-nav-link',
        content: 'Navigate to Settings.',
      },
      {
        target: '.section-templates-tab',
        content: 'Click on Section Templates.',
      },
      {
        target: '.new-template-button',
        content: 'Create a new template.',
      },
      {
        target: '#template-name-input',
        content: 'Give it a name like "Kitchen Remodel - Premium".',
      },
      {
        target: '.add-template-item-button',
        content: 'Add line items with services and pricing.',
      },
    ],
    sectionTemplateUsageTour: [
      {
        target: '.new-quote-button',
        content: 'Create a new quote.',
      },
      {
        target: '.add-from-template-button',
        content: 'Click to add a pre-built section.',
      },
      {
        target: '.template-selector',
        content: 'Select the template you created.',
      },
      {
        target: '.template-apply-button',
        content: 'Apply it to your quote to save time.',
      },
    ],
    emailTemplateEditingTour: [
      {
        target: '.settings-nav-link',
        content: 'Go to Settings.',
      },
      {
        target: '.email-templates-tab',
        content: 'Click on Email Templates.',
      },
      {
        target: '.edit-quote-email-button',
        content: 'Edit the quote email template.',
      },
      {
        target: '.template-body-editor',
        content: 'Customize the message with your branding and signature.',
      },
      {
        target: '.save-template-button',
        content: 'Save your changes.',
      },
    ],
    proposalAcceptanceTour: [
      {
        target: '.quotes-nav-link',
        content: 'Go to your Quotes page.',
      },
      {
        target: '.sent-quotes-section',
        content: 'View quotes you\'ve sent to clients.',
      },
      {
        target: '.quote-status-badge',
        content: 'Watch for status changes. When a client accepts, it shows here.',
      },
      {
        target: '.accepted-quote-action',
        content: 'Once accepted, you can convert it to an invoice.',
      },
    ],
  },

  // Get all checklist items (used for progress calculation)
  get checklist() {
    return [...this.phase1.checklist, ...this.phase2.checklist];
  },
};
